// DS5.1
import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { Url, URLS } from '@wix/da-url';
import Logo from '@wix/da-ds/pkg/Logo';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { useSelector } from 'react-redux';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import DreamUpStars from '@wix/da-ds/pkg/Icons/24x24/DreamUpStars';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { BiData } from '@wix/da-bi/pkg/events';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { useMediaQuery } from '@wix/da-hooks/pkg/useMediaQuery';
import { getCurrentUserHasCore, getTheme, getThemeSkin } from '../../publicSession/selectors';
import SiteHeaderPlacement from '../../DealerAd/Placement/SiteHeader';
import BuyCoreTextButton from '../../Button/core/dsPresets/BuyCoreTextButton';
import { useCurrentPromo } from '../../utils/hooks/useCurrentPromo';
import HeaderSearchInput from '../_partials/HeaderSearchInput';
import Burger from '../_partials/Burger';
import CartIconButton from '../_partials/CartIconButton';
import MoreMenu from '../_partials/MoreMenu';
import WatchButton from '../_partials/WatchButton';
import SubmitMenu from '../_partials/SubmitMenu';
import UserMenuDesktop from '../_partials/UserMenu/UserMenuDesktop';
import NotesButtonAndDropdown from '../_partials/NotesButtonAndDropdown';
import NotificationsDropdownWrapper from '../_partials/NotificationsDropdownWrapper';
import { getBackgroundVariant } from '../../DeviationBackground/redux/helpers';
import s from './SiteHeaderDesktop.scss';
export const DEALER_PROD_REALESTATEID = '9c14ea7a-8c35-429a-9857-00ad04ebb4c1';
const SiteHeader = ({ searchQuery, searchBasePath, hasCoreMembership, isLoggedIn, showNetworkBar = true, cartCount, canSeeOldWatch, isShowingDreamUpLink })=>{
    const { t } = useTranslation();
    const hasCore = useSelector(getCurrentUserHasCore);
    const themeVariant = useSelector((state)=>getBackgroundVariant(getTheme(state), getThemeSkin(state)));
    const { aiContentFilter } = useContext(SessionContext);
    const shouldHideCoreButtonDiscount = useMediaQuery('only screen and (max-width: 1200px) ');
    const logoUrl = Url.buildFromPath(URLS.ROOT);
    const canSeeWatchButton = isLoggedIn && canSeeOldWatch;
    const coreCta = /*#__PURE__*/ React.createElement(BuyCoreTextButton, {
        biData: BiData({
            evid: 375,
            referral_info: ReferralType.HEADER
        }),
        size: "large",
        className: s['buy-core-button'],
        withSaleSparkles: true,
        suppressPromo: shouldHideCoreButtonDiscount
    }, /*#__PURE__*/ React.createElement("span", {
        className: s['long-copy']
    }, t('siteHeader.upgradeToCore')), /*#__PURE__*/ React.createElement("span", {
        className: s['short-copy']
    }, t('common.getCore')));
    let showDreamUpLink = false;
    // for non-core, show the discount promo instead if there is one
    const noPromoAvailable = !useCurrentPromo('self');
    const rotate_topnav_link = useFlag('rotate_topnav_link');
    if (!isLoggedIn) {
        // anon
        showDreamUpLink = true;
    } else if (hasCore) {
        // core
        showDreamUpLink = aiContentFilter !== 'suppress';
    } else if (rotate_topnav_link) {
        // non-core
        showDreamUpLink = aiContentFilter !== 'suppress' && !!isShowingDreamUpLink;
    } else {
        // non-core while checking for sales
        showDreamUpLink = aiContentFilter !== 'suppress' && noPromoAvailable;
    }
    return /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "SiteHeaderDesktop"
    }, /*#__PURE__*/ React.createElement("header", {
        role: "banner",
        className: s['root']
    }, /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "SiteHeaderDesktopBurger"
    }, /*#__PURE__*/ React.createElement(Burger, {
        showNetworkBar: showNetworkBar,
        iconClassName: s['icon']
    })), /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "SiteHeaderDesktopLeft"
    }, /*#__PURE__*/ React.createElement(BiLink, {
        biData: BiData({
            evid: 700,
            link_url: logoUrl,
            link_name: 'logo'
        }),
        href: logoUrl,
        "aria-label": t('siteHeader.a11y.home'),
        className: s['logo-link']
    }, /*#__PURE__*/ React.createElement(Logo, {
        size: "tiny",
        className: s['logo'],
        markColorClassname: s['logo-mark'],
        textColorClassname: s['logo-text'],
        themeVariant: themeVariant,
        allowCustomization: true
    })), /*#__PURE__*/ React.createElement("div", {
        className: s['divider']
    }), canSeeWatchButton && /*#__PURE__*/ React.createElement(WatchButton, {
        className: c(s['text-button'], s['regular-text-button'])
    }), /*#__PURE__*/ React.createElement(TextButton, {
        size: "medium",
        variant: "main",
        className: c(s['text-button'], s['regular-text-button']),
        href: Url.shopLink(),
        biData: BiData({
            evid: 700,
            link_url: Url.shopLink(),
            link_name: 'shop'
        })
    }, t('siteHeader.shop')), /*#__PURE__*/ React.createElement("div", {
        className: s['divider']
    }), isLoggedIn && /*#__PURE__*/ React.createElement(NotificationsDropdownWrapper, {
        className: c(s['icon-button'], s['icon-button-has-submenu']),
        iconClassName: s['icon'],
        numberClassName: s['number']
    }), /*#__PURE__*/ React.createElement(HeaderSearchInput, {
        className: c(s['search-input']),
        searchQuery: searchQuery,
        searchBasePath: searchBasePath
    }), showDreamUpLink && /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
        className: s['dreamup-link'],
        href: Url.dreamupLink(),
        biData: BiData({
            evid: 700,
            link_url: Url.dreamupLink(),
            link_name: 'anon_dreamup'
        })
    }, /*#__PURE__*/ React.createElement(DreamUpStars, {
        className: s['dreamup-link-icon'],
        disableDefaultColors: true
    }), /*#__PURE__*/ React.createElement("span", {
        className: s['long-copy']
    }, t('siteHeader.anonDreamup')), /*#__PURE__*/ React.createElement("span", {
        className: s['short-copy']
    }, t('siteHeader.anonDreamup.short'))), !showDreamUpLink && !hasCoreMembership && coreCta), /*#__PURE__*/ React.createElement("div", {
        className: s['right-side']
    }, /*#__PURE__*/ React.createElement(ErrorBoundary, {
        debugComponent: "SiteHeaderDesktopRight"
    }, /*#__PURE__*/ React.createElement(SiteHeaderPlacement, {
        className: s['ad-slot']
    }), !isLoggedIn && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(TextButton, {
        size: "small",
        variant: "main",
        biData: BiData({
            evid: 240,
            referral_info: ReferralType.HEADER
        }),
        className: s['text-button'],
        href: Url.joinLink()
    }, t('siteHeader.join')), /*#__PURE__*/ React.createElement(TextButton, {
        size: "small",
        variant: "main",
        biData: BiData({
            evid: 700,
            link_url: Url.loginLink(),
            link_name: 'login'
        }),
        className: s['text-button'],
        href: Url.loginLink()
    }, t('siteHeader.signIn'))), /*#__PURE__*/ React.createElement(MoreMenu, {
        isLoggedIn: !!isLoggedIn,
        className: c(s['icon-button'], s['more-menu-button']),
        menuClassName: s['more-menu']
    }), isLoggedIn && /*#__PURE__*/ React.createElement(NotesButtonAndDropdown, {
        className: c(s['icon-button'], s['icon-button-has-submenu']),
        iconButtonClassName: s['icon'],
        numberClassName: s['number']
    }), cartCount > 0 && /*#__PURE__*/ React.createElement(CartIconButton, {
        className: s['icon-button'],
        cartCount: cartCount,
        iconClassName: s['icon'],
        numberClassName: s['number']
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['user-icon-button-wrapper']
    }, /*#__PURE__*/ React.createElement(UserMenuDesktop, {
        isOpen: false,
        className: s['icon-submenu'],
        disableThemeSwitcher: false
    })), /*#__PURE__*/ React.createElement(SubmitMenu, {
        isOpen: false,
        className: c(s['icon-button-has-submenu'], s['icon-button-has-submit-submenu']),
        menuClassName: s['icon-submenu']
    })))));
};
SiteHeader.displayName = 'SiteHeader';
export default SiteHeader;

//# sourceMappingURL=SiteHeaderDesktop.js.map